import React from 'react';
import PropTypes from 'prop-types';
import {
  HouseSvg,
  PaperSvg,
  CirclePlus,
  DoubleChevronIconRight,
  DoubleChevronIconLeft,
} from 'AppSrc/core/components/common/svg-icons';
import { COORDINATOR } from 'AppSrc/core/components/forms/form-base-components/data-structure/constants';
import { MOBILE, FIXED } from 'AppSrc/core/components/forms/value-dependency-constants';
import { orderedGroupsSelector } from 'AppSrc/core/auth/auth-selectors';
import { connect } from 'react-redux';
import 'AppSrc/core/style/components/side-nav/side-nav.css';
import { SideNavMenuItem } from './side-nav-menu-item';

const CRQS_PATH = '/crqs';

const MY_CRQ_PATH = '/mycrq';

const homeNavItem = {
  path: '/',
  SVG: HouseSvg,
  text: 'Home',
  exact: true,
};

const crqsNavItemBase = {
  SVG: PaperSvg,
  text: 'CRQs',
  matchPath: CRQS_PATH,
};

const MycrqsNavItemBase = {
  SVG: PaperSvg,
  text: 'MyCRQs',
  matchPath: MY_CRQ_PATH,
};


const createNavItem = {
  path: '/crq/create',
  SVG: CirclePlus,
  text: 'Create CRQ',
  exact: false,
};

const myFixedCRQ = {
  name: 'My Fixed CRQs',
  network: 'fixed',
};

const myMobileCRQ = {
  name: 'My Mobile CRQs',
  network: 'mobile',
};

// sub items for selecting crq status to filter on
// (open, closed, cancelled)
const statusSubItems = [
  {
    pathParam: '',
    text: 'Opened',
    exact: true,
  },
  {
    pathParam: '/closed',
    text: 'Closed',
    exact: true,
  },
  {
    pathParam: '/cancelled',
    text: 'Cancelled',
    exact: true,
  },
];

// get the status subitems for a base path
// eg if the base path is `/crqs/` the 'closed' status subitem will have
// a path of `/crqs/closed`
const getStatusSubItemsForPath = (basePath) => statusSubItems.map((subItem) => ({
  ...subItem,
  path: `${basePath}${subItem.pathParam}`,
}));

const networkSuffixes = {
  [MOBILE]: ' (M)',
  [FIXED]: ' (F)',
};

// get sidenav items for a coordinator group.
// item should have subitems for status (open, closed, cancelled)
const getGroupItem = (group) => {
  const { name, network } = group;
  const path = `${CRQS_PATH}/${network}/${name}`;
  const text = `${name} ${networkSuffixes[network]}`;
  return ({
    path,
    text,
    subItems: getStatusSubItemsForPath(path),
  });
};

// get sidenav items for a network
// item should have subitems for status (open, closed, cancelled)
const getNetworkItem = (network) => {
  const path = `${CRQS_PATH}/${network}/all`;
  return ({
    path,
    text: network.toUpperCase(),
    subItems: getStatusSubItemsForPath(path),
  });
};
/*
  get the subitems for the CRQs nav item.
  The contents will depend on the users roles as specified here:
  https://ocasta.atlassian.net/browse/VTO-743
  If the user is requester on both networks there should be a subitem for
  each status:
  - crqs
  -- opened
  -- closed
  -- cancelled
  If the user is coordinator on both networks, there should be a subitem for
  each of the users groups, and then for each status:
  - crqs
  -- group1
  --- opened
  --- closed
  --- cancelled
  -- group2
  ....
  If the user is coordinator on only one network it should be the same as above
  but with a final item for the network on which they are a requester
  - crqs
  -- group1
  ...
  -- MOBILE
  --- opened
  ...
*/
const getCrqsSubItems = ({
  orderedGroups,
  isCoordinatorMobile,
  isCoordinatorFixed,
}) => {
  const requesterForBothNetworks = !isCoordinatorFixed && !isCoordinatorMobile;
  if (requesterForBothNetworks) {
    // as a requester we don't have to provide a coordinator group in the urls
    const basePath = `${CRQS_PATH}/all/all`;
    return getStatusSubItemsForPath(basePath);
  }
  const items = orderedGroups.map((group) => getGroupItem(group));
  // if user is only coordinator on one network, the final side nav entry
  // should be a link to the network on which they are requester
  if (!isCoordinatorMobile) {
    items.push(getNetworkItem(MOBILE));
  }
  if (!isCoordinatorFixed) {
    items.push(getNetworkItem(FIXED));
  }
  return items;
};


const getMYCRQGroupItem = (group, isCoordinatorMobile, isCoordinatorFixed) => {
  const { name, network } = group;
  if (name === myFixedCRQ.name && isCoordinatorFixed) {
    const path = `${MY_CRQ_PATH}/${network}/${name}`;
    const text = `${name} ${networkSuffixes[network]}`;
    return ({
      path,
      text,
      subItems: getStatusSubItemsForPath(path),
    });
  }
  if (name === myMobileCRQ.name && isCoordinatorMobile) {
    const path = `${MY_CRQ_PATH}/${network}/${name}`;
    const text = `${name} ${networkSuffixes[network]}`;
    return ({
      path,
      text,
      subItems: getStatusSubItemsForPath(path),
    });
  }
  const path = `${MY_CRQ_PATH}/${network}/${name}`;
  const text = `${name} ${networkSuffixes[network]}`;
  return ({
    path,
    text,
    subItems: getStatusSubItemsForPath(path),
  });
};

const getMYCRQSubItems = ({
  mycrqItems,
  isCoordinatorMobile,
  isCoordinatorFixed,
}) => {
  const items = mycrqItems.map((group) => getMYCRQGroupItem(group, isCoordinatorMobile, isCoordinatorFixed));
  return items;
};

const getCrqsNavItemPath = (orderedGroups) => {
  if (!orderedGroups || orderedGroups.length === 0) {
    return `${CRQS_PATH}/all/all`;
  }
  // get the group and network for the first group
  const { name, network } = orderedGroups[0];
  return `${CRQS_PATH}/${network}/${name}`;
};

const getNavMenuItems = ({
  orderedGroups,
  isCoordinatorMobile,
  isCoordinatorFixed,
}) => {
  const subItems = getCrqsSubItems({
    orderedGroups,
    isCoordinatorMobile,
    isCoordinatorFixed,
  });
  const mycrqItems = [myFixedCRQ, myMobileCRQ];
  const mycrqSubItems = getMYCRQSubItems({
    mycrqItems,
    isCoordinatorMobile,
    isCoordinatorFixed,
  });
  const crqsPath = getCrqsNavItemPath(orderedGroups);
  const crqsNavItem = {
    ...crqsNavItemBase,
    path: crqsPath,
    subItems,
  };
  const MycrqsNavItem = {
    ...MycrqsNavItemBase,
    path: '/mycrq/fixed/My%20Fixed%20CRQs',
    subItems: mycrqSubItems,
  };
  if (isCoordinatorFixed || isCoordinatorMobile) {
    return [
      homeNavItem,
      crqsNavItem,
      MycrqsNavItem,
      createNavItem,
    ];
  }
  // for requester view myCrqs is not applicable.
  return [
    homeNavItem,
    crqsNavItem,
    createNavItem,
  ];
};

const SideNavDisplay = React.memo(({
  toggleNavOpen,
  open,
  orderedGroups,
  isCoordinatorMobile,
  isCoordinatorFixed,
}) => {
  const navMenuItems = getNavMenuItems({
    orderedGroups,
    isCoordinatorMobile,
    isCoordinatorFixed,
  });
  const className = 'side-nav-container';
  const classNameState = `${className}${open ? '-open' : '-closed'}`;
  return (
    <>
      <div className={`${className} ${className}-spacemaker ${classNameState}`} />
      <div className={`${className} ${classNameState}`}>
        <div className="side-nav">
          <div className="side-nav-buttons">
            {
              navMenuItems.map((navMenuItem) => (
                <SideNavMenuItem
                  key={navMenuItem.path}
                  menuItem={navMenuItem}
                  menuOpen={open}
                />
              ))
            }
          </div>
          <div className="toggle-side-nav-wrapper">
            <button
              type="button"
              onClick={toggleNavOpen}
            >
              {
                open ? (
                  <DoubleChevronIconLeft />
                ) : (
                  <DoubleChevronIconRight />
                )
              }
            </button>
          </div>
        </div>
      </div>
    </>
  );
});

SideNavDisplay.propTypes = {
  toggleNavOpen: PropTypes.func,
  open: PropTypes.bool,
  orderedGroups: PropTypes.arrayOf(PropTypes.object),
  isCoordinatorMobile: PropTypes.bool,
  isCoordinatorFixed: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => {
  const { roles } = state.auth.user;
  const orderedGroups = orderedGroupsSelector(state);
  const isCoordinatorMobile = roles[MOBILE] === COORDINATOR;
  const isCoordinatorFixed = roles[FIXED] === COORDINATOR;
  return ({
    orderedGroups,
    isCoordinatorMobile,
    isCoordinatorFixed,
  });
};

const SideNav = connect(
  mapStateToProps,
)(SideNavDisplay);

export { SideNav, SideNavDisplay };

